// The season timelapse in the public reader: card 10 of mockup round 076
// (chronicle.md §9.4, ui-map-interface.md §3.6, R1620).
//
// A card heads the season page of a shared volume: THE SEA · DAY 1 TO
// DAY n, the small map of the frames, the five banners at the frame, the
// transport and the day marks. The legend marks the faction of the
// volume's commander. Each day page carries ▸ ON THE MAP, which moves the
// scrubber to the first frame of that day and brings the card into view,
// so the story and the sea meet.
//
// The reader reads the public route (chronicle.md §10.5). A volume whose
// season stored no frame shows no card and no ▸ ON THE MAP.

import { TIMELAPSE_READER_MAP_PX, TIMELAPSE_TICKS } from '../../gen/presentation';
import { timelapse as T } from '../../gen/strings';
import type { Timelapse } from '../../net/timelapse';
import { reducedMotion } from '../../render/motion';
import { button } from '../choice/choice';
import { esc } from '../esc';
import { FrameCanvas } from './chart';
import { IntroSting } from './intro';
import {
  dayFrame, dayTicks, frameCounts, frameStamp, legendHtml, legendRows, openingFrame, seasonDay,
  ticksHtml,
} from './model';
import { TimelapsePlayer } from './player';
import { TransportBinding, transportHtml } from './transport';
import { MarkupHost } from '../markuphost';

/** The glyph of the card's head, beside the glyphs of the book's cards. */
const GLYPH = '≈';

/** The button of a day page that moves the scrubber to that day: a kit
 *  button (R2011). The reader routes it by `data-tl`. */
export function onTheMapButton(day: number): string {
  return button(T.on_the_map, { act: 'tlday', data: { tl: 'day', day: String(day) } },
    { variant: 'primary', size: 'compact', cls: 'onmap' });
}

export class ReaderTimelapse {
  readonly el: HTMLElement;
  readonly player = new TimelapsePlayer();
  private readonly canvas = new FrameCanvas(TIMELAPSE_READER_MAP_PX);
  private readonly binding: TransportBinding;
  /** The legend beside the small map. */
  private readonly legend: MarkupHost;
  /** The frame the map drew last, so a step of the sting draws no map. */
  private drawnFrame = -1;
  /** The sting before frame 0, over the small map (R1655). */
  private readonly sting: IntroSting;

  /** `faction` is the commander's faction, 0 when the read names none. */
  constructor(
    private readonly tl: Timelapse,
    private readonly faction: number,
    private readonly commander: string,
  ) {
    const n = tl.frames.length;
    const first = tl.frames[0].tSec;
    const last = tl.frames[n - 1].tSec;
    // The card opens on the sea that ended the season: the newest frame
    // that holds an owned cell, before the settlement drained it.
    this.player.setMax(n - 1, true);
    this.player.home = openingFrame(tl);
    this.player.seek(this.player.home);
    this.el = document.createElement('div');
    this.el.className = 'card tlread';
    this.el.innerHTML = `<div class="h"><span class="g">${GLYPH}</span>`
      + `${esc(T.reader_head({ from: seasonDay(first), to: seasonDay(last) }))}`
      + `<span class="sp"></span><span class="more">${esc(T.reader_frames({ frames: n }))}</span></div>`
      + '<div class="tlb"><div class="tlmap"></div><div class="tlside"><div class="tlleg"></div>'
      + transportHtml(this.player, frameStamp(tl.frames[this.player.frame].tSec))
      + ticksHtml(dayTicks(first, last, TIMELAPSE_TICKS))
      + '</div></div>';
    this.el.querySelector('.tlmap')?.append(this.canvas.el);
    this.legend = new MarkupHost(this.el.querySelector<HTMLElement>('.tlleg')!);
    this.binding = new TransportBinding(this.el, this.player, {
      stamp: (f) => frameStamp(tl.frames[f]?.tSec ?? 0),
    });
    this.sting = new IntroSting(this.player, () => this.el.querySelector<HTMLElement>('.tlmap'));
    this.player.on(() => { this.draw(); this.sting.update(); });
  }

  /** Draws the frame under the playhead. The page calls it once the card
   *  stands in the document, so the canvas has its laid-out width. */
  draw(): void {
    const f = this.tl.frames[this.player.frame];
    if (!f) return;
    // A step of the sting moves no frame: the transport follows, the map
    // stands (R1655).
    if (this.player.intro >= 0 && this.player.frame === this.drawnFrame) {
      this.binding.update();
      return;
    }
    this.drawnFrame = this.player.frame;
    this.canvas.draw(f.cells, this.tl.cols, this.tl.rows);
    // The mark names the commander; a read that names none marks nothing.
    const mark = this.commander === '' ? 0 : this.faction;
    const title = T.legend_commander({ commander: this.commander });
    this.legend.write(legendHtml(legendRows(frameCounts(f.cells), mark), title));
    this.binding.update();
  }

  /** ▸ ON THE MAP: the first frame of one day page, and the card in view. */
  showDay(day: number): void {
    const i = dayFrame(this.tl, day);
    if (i < 0) return;
    this.player.seek(i);
    this.el.scrollIntoView?.({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }
}
